import { useState, useRef } from 'react';

import { MenuItem } from '@chakra-ui/react';
import styled from '@emotion/styled/macro';
import { FormattedMessage, useIntl } from 'react-intl';

import { uploadFile } from '../../services/items';

import useCommonToast from '../../hooks/useCommonToast';

import Tooltip from '../utils/Tooltip';
import CircularIconButton from '../utils/CircularIconButton';
import SecondaryIconButton from '../utils/SecondaryIconButton';

import { ReactComponent as UploadIcon } from '../../icons/upload.svg';
import { ReactComponent as CircledUploadIcon } from '../../icons/upload-circled.svg';

const UploadButton = ({
    itemId,
    type,
    hasLabel,
    isInsideMenu = false,
    onTemplatesConfigChange,
    onAfterUploadChange,
    ...rest
}) => {
    const intl = useIntl();
    const toast = useCommonToast();

    const [importLoading, setImportLoading] = useState(false);

    const inputFileRef = useRef();
    const loadingImportText = intl.formatMessage({ id: 'common_loading' });

    const importData = async (data) => {
        setImportLoading(true);
        const file = data.target.files[0];
        try {
            await uploadFile(type, itemId, file);
            toast(intl.formatMessage({ id: 'import_file_success' }));

            // for bidding strategy we need to update the templates config for the given sub item to null (hence ='Custom' strategy)
            if (type === 'batteries') {
                onTemplatesConfigChange(itemId, { templateId: null, hasUpload: true });
            } else if (type === 'hedges' || type === 'basis') {
                onAfterUploadChange();
            }
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'import_file_fail' }),
                });
            }
        }

        if (inputFileRef.current) {
            inputFileRef.current.value = '';
        }

        setImportLoading(false);
    };

    const openFileSelection = () => {
        inputFileRef.current.click();
    };

    return (
        <>
            {isInsideMenu ? (
                <MenuItem key="upload-btn" textTransform="capitalize" onClick={openFileSelection} {...rest}>
                    <FormattedMessage id="common_upload_btn_tooltip_and_label" />
                </MenuItem>
            ) : hasLabel ? (
                <SecondaryIconButton
                    leftIcon={<UploadIcon />}
                    size="sm"
                    type="button"
                    variant="secondary"
                    onClick={openFileSelection}
                    isLoading={importLoading}
                    loadingText={loadingImportText}
                    {...rest}
                >
                    <FormattedMessage id="common_upload_btn_tooltip_and_label" />
                </SecondaryIconButton>
            ) : (
                <TooltipWrapper>
                    <Tooltip label={intl.formatMessage({ id: 'common_upload_btn_tooltip_and_label' })}>
                        <CircularIconButton
                            aria-label={intl.formatMessage({ id: 'common_upload_btn_tooltip_and_label' })}
                            icon={<CircledUploadIcon />}
                            onClick={openFileSelection}
                            isLoading={importLoading}
                            mr={6}
                        />
                    </Tooltip>
                </TooltipWrapper>
            )}
            <input
                type="file"
                ref={inputFileRef}
                style={{ display: 'none' }}
                onChange={(value) => {
                    importData(value);
                }}
            />
        </>
    );
};

const TooltipWrapper = styled.div`
    .tooltip_wrapper {
        transform: translate(-70%, 110%);
        &:focus {
            transform: translate(-70%, 110%);
        }
    }

    .tooltip_children_wrapper + div {
        transform: translate(-70%, 110%);
        &:focus {
            transform: translate(-70%, 110%);
        }
    }
`;

export default UploadButton;
